import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { useLegal } from "@/entities/profile";
import { ApiError } from "@/shared/api";
import { formatDate } from "@/shared/lib";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Правовой документ. Figma: `Profile · Legal & Risk` (27:193).
 *
 * `body` приходит в Markdown, поэтому нужен рендерер. Он грузится отдельным
 * чанком: правовые документы открывают редко, а тянуть парсер Markdown в
 * основной бандл ради этого — платить за него на каждом запуске.
 */
const Markdown = lazy(() => import("react-markdown"));

export function LegalPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { data, isPending, isError, error, refetch } = useLegal(slug ?? "");

  if (isPending) return <ScreenSkeleton blocks={[48, 320]} />;

  if (isError) {
    // Неизвестный слаг бэкенд отдаёт как 404 — это не сбой, а «нет такого».
    const notFound = error instanceof ApiError && error.code === "NOT_FOUND";
    return (
      <ScreenState
        tone={notFound ? "info" : "loss"}
        icon={notFound ? "questionMark" : "warningFill"}
        title={t(notFound ? "profile.legal.notFound.title" : "states.error.title")}
        body={t(notFound ? "profile.legal.notFound.body" : "states.error.body")}
        {...(notFound ? {} : { action: { label: t("states.error.action"), onClick: () => void refetch() } })}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar
        title={data.title}
        subtitle={t("profile.legal.updated", { date: formatDate(data.updated_at.slice(0, 10)) })}
      />

      <article className="flex flex-col gap-3 rounded-3xl border border-border-subtle bg-background-elevated p-5 text-caption leading-relaxed font-normal text-text-secondary [&_h1]:text-h3 [&_h1]:font-semibold [&_h1]:text-text-primary [&_h2]:text-label [&_h2]:font-semibold [&_h2]:text-text-primary [&_li]:ml-4 [&_li]:list-disc [&_strong]:text-text-primary">
        <Suspense fallback={<ScreenSkeleton blocks={[280]} />}>
          <Markdown>{data.body}</Markdown>
        </Suspense>
      </article>
    </div>
  );
}
